import { useCallback } from "react";
import useAuthStore from "Store/useAuthStore";
import analytics from "Utils/analytics";

/**
 * useAnalytics
 *
 * Binds the two properties every Mixpanel call site repeats — the signed-in
 * user's email and a `from` screen id — so a tracked interaction is one line
 * carrying only what is actually specific to it.
 *
 * Before:
 *   const email = useAuthStore((s) => s.email);
 *   analytics.trackPAICRoleImpactViewed({ from: "pac_role_impact", email, roleId });
 *
 * After:
 *   const track = useAnalytics("pac_role_impact");
 *   track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.ROLE_IMPACT_VIEWED, { roleId });
 *
 * `from` is bound once per screen because that is exactly how it is used:
 * a per-file constant, hardcoded identically at every call site in that file.
 * A forgotten `email` or a typo'd `from` fails silently — the event still
 * reaches Mixpanel, just unattributable — so binding them once is what stops
 * the failure happening at all rather than being caught in review.
 *
 * `description` defaults to "" to match the shape the existing wrappers in
 * Utils/analytics send, and is overridable like any other property since the
 * caller's properties spread last.
 *
 * Returns a stable callback (same identity while `from`/`email` hold), so it
 * is safe in a useEffect/useCallback dependency array without re-firing.
 *
 * Components only — anything outside React (plain utils, zustand actions)
 * calls `analytics.track(eventName, { from, email, ... })` directly.
 *
 * @param {string} from Screen id, snake_case (e.g. "org_design_workspace").
 * @returns {(eventName: string, properties?: object) => void}
 */
const useAnalytics = (from) => {
  const email = useAuthStore((state) => state.email);

  return useCallback(
    (eventName, properties = {}) =>
      analytics.track(eventName, { from, email, description: "", ...properties }),
    [from, email]
  );
};

export default useAnalytics;
